import React, { useState } from 'react';
import { Button } from '@roger/ui-components';
import { Check, Undo2, Loader2 } from 'lucide-react';
import { useIntake } from '../../hooks/useIntake';
import { useToast } from '../../hooks/useToastContext';
import type { IntakeTabKey } from '../../services/api/types.gen';

interface MarkCompletedButtonProps {
  intakeId: string;
  tabKey: IntakeTabKey;
  isComplete: boolean;
  onStatusChange?: () => void | Promise<void>;
  className?: string;
  disabled?: boolean;
  /** Override label for the "mark complete" action (e.g. "Mark Complete without Onboarding") */
  completeButtonLabel?: string;
}

const MarkCompletedButton: React.FC<MarkCompletedButtonProps> = ({
  intakeId,
  tabKey,
  isComplete,
  onStatusChange,
  className = '',
  disabled: disabledProp = false,
  completeButtonLabel = 'Mark Complete',
}) => {
  const { confirmTabStatus } = useIntake();
  const { error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const disabled = disabledProp || isLoading;

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      const newStatus = isComplete ? 'incomplete' : 'complete';
      await confirmTabStatus(intakeId, tabKey, newStatus);
      if (onStatusChange) {
        await onStatusChange();
      }
    } catch (error) {
      console.error('Failed to update tab status:', error);
      showError(
        'Failed to update status',
        'Please try again or refresh the page.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isComplete) {
    return (
      <Button
        variant='outline'
        onClick={handleToggle}
        disabled={disabled}
        className={className}
      >
        {isLoading ? (
          <>
            <Loader2 className='h-4 w-4 animate-spin' />
            Updating...
          </>
        ) : (
          <>
            <Undo2 className='h-4 w-4' />
            Mark Incomplete
          </>
        )}
      </Button>
    );
  }

  return (
    <Button
      variant='default'
      onClick={handleToggle}
      disabled={disabled}
      className={className}
    >
      {isLoading ? (
        <>
          <Loader2 className='h-4 w-4 animate-spin' />
          Updating...
        </>
      ) : (
        <>
          <Check className='h-4 w-4' />
          {completeButtonLabel}
        </>
      )}
    </Button>
  );
};

export default MarkCompletedButton;
